import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { BillingCycle, type CatalogBillingCycle } from '@etiquetachef/types';
import { adminCreateCheckoutLinkCallable } from '@/lib/callables';
import { formatDateTime } from '@/lib/format';
import { toast } from '@/lib/notifications';

type Props = {
  open: boolean;
  tenantId: string;
  tenantName: string;
  onClose: () => void;
  /** Chamado após gerar, para o card de links recarregar. */
  onCreated?: () => void;
};

/**
 * Gera um link de pagamento (Stripe Checkout) para um tenant de venda direta
 * que não concluiu o checkout, e o deixa na tela para copiar.
 *
 * Origem: #289 — 2026-08-29 14:00 +0200.
 *
 * Por quê: a sessão criada é IDÊNTICA à que o cliente abriria sozinho
 * (assinatura, Pix Automático, locale do tenant): deliberadamente não é um
 * Payment Link avulso da Stripe, porque em modo avulso não há mandato de Pix
 * Automático — o Pix viraria pagamento único e a assinatura não ativaria. O
 * link é SEMPRE do Stripe: a venda pelo Asaas nasce só pela Contratação do
 * CRM (#752).
 *
 * Contrato: `adminCreateCheckoutLinkCallable({ tenantId, billing_cycle })`,
 * ciclo ∈ {monthly, yearly} — só os com preço de lista (#679 D13). Sucesso →
 * fica aberto com `url` + `expires_at` e chama `onCreated` (o card de links
 * recarrega). O servidor recusa com assinatura viva ("1 tenant = 1
 * subscription") e em venda assistida ainda não paga (`isUnpaidAssistedSale`,
 * #679/#700: ela paga pelo link negociado do CRM) — `BillingActions` já
 * desabilita o botão nesses casos.
 *
 * O servidor exige `ADMIN_PERMISSIONS.manageSubscriptions` (ROOT/FINANCE).
 *
 * Usado por: `BillingActions` → `routes/TenantDetailPage.tsx` (rota
 * `/tenants/:id`, menu Tenants), admin web. Spec:
 * `docs/screens/tenant-detail-admin.md`.
 *
 * Dados: `AdminCreateCheckoutLinkInput/Output` (`packages/types`):
 * `{ url, session_id, expires_at: ms | null }`
 * (`apps/functions/src/admin/adminCreateCheckoutLink.ts`).
 *
 * Histórico:
 *  - 2026-09-23 10:06 +0200 — (#700) ciclo restrito a `CatalogBillingCycle`.
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu e permissão (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function CreateCheckoutLinkDialog({
  open,
  tenantId,
  tenantName,
  onClose,
  onCreated,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  // Link de catálogo: só os ciclos com preço de lista (#679 D13).
  const [cycle, setCycle] = useState<CatalogBillingCycle>(BillingCycle.MONTHLY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    url: string;
    expiresAt: number | null;
  } | null>(null);

  function close(): void {
    setError(null);
    setCreated(null);
    setCycle(BillingCycle.MONTHLY);
    onClose();
  }

  /**
   * Gera o link no `adminCreateCheckoutLink` e mantém o diálogo aberto com a URL.
   *
   * Origem: #289 — 2026-08-29 14:00 +0200.
   *
   * Contrato: input `{ "tenantId": "tenant-1", "billing_cycle": "monthly" }`;
   * saída `{ url, session_id, expires_at }` (`expires_at` epoch ms ou `null`).
   * Sucesso → guarda URL/validade, toast e `onCreated` (recarrega
   * `CheckoutLinksCard`). Erro → `err.message` num `Alert` dentro do modal (#1021). Cada clique cria uma sessão
   * nova no Stripe (não é idempotente); o botão some depois do sucesso e fica
   * em `loading` durante o envio.
   *
   * Usado por: botão "Gerar" deste diálogo.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   */
  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const result = await adminCreateCheckoutLinkCallable({
        tenantId,
        billing_cycle: cycle,
      });
      // Fica na tela em vez de fechar: o link é o produto da ação, e o operador
      // precisa copiá-lo antes de sair.
      setCreated({ url: result.url, expiresAt: result.expires_at });
      toast.success(t('billing.checkoutLink.createSuccess'));
      onCreated?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const cycleOptions = [
    { value: BillingCycle.MONTHLY, label: t('billing.checkoutLink.cycleMonthly') },
    { value: BillingCycle.YEARLY, label: t('billing.checkoutLink.cycleYearly') },
  ];

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('billing.checkoutLink.createTitle')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {created
                ? t('billing.checkoutLink.done')
                : t('tenantDetail.actions.cancel')}
            </Button>
            {created ? null : (
              <Button
                variant="primary"
                onClick={(): void => {
                  void handleSubmit();
                }}
                loading={submitting}
              >
                {t('billing.checkoutLink.createConfirm')}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {created ? (
          <>
            <Alert type="success">{t('billing.checkoutLink.createdInfo')}</Alert>
            <FormField label={t('billing.checkoutLink.createdLinkLabel')}>
              <CopyToClipboard
                variant="inline"
                textToCopy={created.url}
                textToDisplay={<Box variant="code">{created.url}</Box>}
                copyButtonAriaLabel={t('common.copy')}
                copySuccessText={t('common.copied')}
                copyErrorText={t('common.copyError')}
              />
            </FormField>
            {created.expiresAt != null ? (
              <Box variant="p" color="text-body-secondary">
                {t('billing.checkoutLink.createdExpiresAt', {
                  date: formatDateTime(created.expiresAt, i18n.language),
                })}
              </Box>
            ) : null}
          </>
        ) : (
          <>
            <Box variant="p" color="text-body-secondary">
              {t('billing.checkoutLink.createDescription', { name: tenantName })}
            </Box>
            <FormField label={t('billing.checkoutLink.cycleLabel')}>
              <Select
                selectedOption={
                  cycleOptions.find((o) => o.value === cycle) ?? cycleOptions[0] ?? null
                }
                options={cycleOptions}
                onChange={({ detail }): void => {
                  setCycle(detail.selectedOption.value as CatalogBillingCycle);
                }}
              />
            </FormField>
            <Alert type="info">{t('billing.checkoutLink.createInfo')}</Alert>
          </>
        )}
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
